import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { ChevronDown, RefreshCw, AlertTriangle, Trash2 } from "lucide-react";

// Statuses settable via the simple chip row (the 5 return/refund
// statuses go through the dedicated panel below).
const STATUSES = ["placed", "confirmed", "packed", "shipped", "delivered", "cancelled"];

// iter39: return/refund admin actions.  `requires.amount` and `online_refund`
// drive whether the Razorpay refund call is made on the backend.
const RETURN_ACTIONS = [
  { action: "request_return",  label: "Mark Return Requested", target: "return_requested", color: "bg-amber-50 text-amber-900 border-amber-200" },
  { action: "approve_return",  label: "Approve Return",         target: "return_approved",  color: "bg-emerald-50 text-emerald-900 border-emerald-200" },
  { action: "reject_return",   label: "Reject Return",          target: "return_rejected",  color: "bg-red-50 text-red-900 border-red-200" },
  { action: "initiate_refund", label: "Initiate Refund",        target: "refund_initiated", color: "bg-blue-50 text-blue-900 border-blue-200" },
  { action: "complete_refund", label: "Mark Refund Completed",  target: "refund_completed", color: "bg-emerald-100 text-emerald-900 border-emerald-300" },
];

const STATUS_PILL = {
  return_requested: { label: "Return Requested",  cls: "bg-amber-100 text-amber-900" },
  return_approved:  { label: "Return Approved",   cls: "bg-emerald-100 text-emerald-900" },
  return_rejected:  { label: "Return Rejected",   cls: "bg-red-100 text-red-900" },
  refund_initiated: { label: "Refund Initiated",  cls: "bg-blue-100 text-blue-900" },
  refund_completed: { label: "Refund Completed",  cls: "bg-emerald-200 text-emerald-900" },
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState({});

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (order_id, status) => {
    try {
      await api.put(`/admin/orders/${order_id}/status`, null, { params: { status } });
      toast.success(`Order marked ${status}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-orders">
      <h1 className="font-serif text-4xl text-brand-dark mb-8">Orders</h1>
      {orders.length === 0 ? (
        <div className="bg-white border border-brand-line rounded-2xl p-10 text-center text-brand-muted text-sm" data-testid="orders-empty">No Orders</div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <AdminOrderRow key={o.order_id} o={o} open={open} setOpen={setOpen} setStatus={setStatus} reload={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function AdminOrderRow({ o, open, setOpen, setStatus, reload }) {
  const pill = STATUS_PILL[o.order_status];
  return (
    <div className="bg-white border border-brand-line rounded-2xl overflow-hidden" data-testid={`admin-order-${o.order_id}`}>
      <button onClick={() => setOpen({ ...open, [o.order_id]: !open[o.order_id] })} className="w-full p-5 flex flex-wrap items-center justify-between gap-3 text-left hover:bg-brand-subtle/50">
        <div>
          <div className="font-mono text-xs text-brand-muted">{o.order_id}</div>
          <div className="font-serif text-lg">{o.user_name}</div>
          <div className="text-xs text-brand-muted">{new Date(o.created_at).toLocaleString()}</div>
        </div>
        <div className="flex items-center gap-4">
          <span className="font-semibold">₹{o.total.toFixed(0)}</span>
          <span className={`text-xs uppercase tracking-wider px-2.5 py-1 rounded-full ${pill ? pill.cls : "bg-brand-subtle"}`}>
            {pill ? pill.label : o.order_status}
          </span>
          <ChevronDown className={`w-4 h-4 transition-transform ${open[o.order_id] ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open[o.order_id] && (
        <div className="border-t border-brand-line p-5 bg-brand-cream/40 space-y-5 text-sm">
          <div>
            <h4 className="font-medium mb-2">Items</h4>
            {o.items.map((it, i) => (
              <div key={`${it.product_id || it.name}-${it.variant_label || ""}-${i}`} className="flex justify-between">
                <span>{it.name}{it.variant_label ? ` · ${it.variant_label}` : ""} × {it.quantity}</span>
                <span>₹{Number(it.line_total ?? (it.price || 0) * (it.quantity || 0)).toFixed(0)}</span>
              </div>
            ))}
          </div>
          {/* iter53 — full Customer Address block (every field captured at
              checkout). Placed BELOW Items, ABOVE Shipping per spec. */}
          <div data-testid={`order-customer-address-${o.order_id}`}>
            <h4 className="font-medium mb-2">Customer Address</h4>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-brand-muted">
              <div><dt className="inline font-medium text-brand-dark">Name:</dt> <dd className="inline">{o.address?.full_name || "—"}</dd></div>
              <div><dt className="inline font-medium text-brand-dark">Phone:</dt> <dd className="inline">{o.address?.phone || "—"}</dd></div>
              {o.customer_email && (
                <div className="sm:col-span-2"><dt className="inline font-medium text-brand-dark">Email:</dt> <dd className="inline">{o.customer_email}</dd></div>
              )}
              <div><dt className="inline font-medium text-brand-dark">House / Flat No.:</dt> <dd className="inline">{o.address?.house_no || "—"}</dd></div>
              <div><dt className="inline font-medium text-brand-dark">Street / Area:</dt> <dd className="inline">{o.address?.street || o.address?.line1 || "—"}</dd></div>
              {o.address?.landmark && (
                <div className="sm:col-span-2"><dt className="inline font-medium text-brand-dark">Landmark:</dt> <dd className="inline">{o.address.landmark}</dd></div>
              )}
              <div><dt className="inline font-medium text-brand-dark">City:</dt> <dd className="inline">{o.address?.city || "—"}</dd></div>
              <div><dt className="inline font-medium text-brand-dark">State:</dt> <dd className="inline">{o.address?.state || "—"}</dd></div>
              <div><dt className="inline font-medium text-brand-dark">Pincode:</dt> <dd className="inline">{o.address?.pincode || "—"}</dd></div>
              <div><dt className="inline font-medium text-brand-dark">Country:</dt> <dd className="inline">{o.address?.country || "India"}</dd></div>
            </dl>
          </div>
          <div>
            <h4 className="font-medium mb-1">Shipping</h4>
            <p className="text-brand-muted leading-relaxed">{o.address.full_name}, {o.address.line1}, {o.address.city}, {o.address.state} {o.address.pincode} · {o.address.phone}</p>
          </div>
          <div>
            <h4 className="font-medium mb-2">Payment</h4>
            <p className="text-brand-muted">{o.payment_method.toUpperCase()} · {o.payment_status}{o.razorpay_payment_id ? ` · pay ${o.razorpay_payment_id}` : ""}</p>
          </div>
          <div>
            <h4 className="font-medium mb-2">Update status (fulfilment)</h4>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => setStatus(o.order_id, s)} className={`text-xs px-3 py-1.5 rounded-full uppercase tracking-wider ${o.order_status === s ? "bg-brand-chili text-white" : "bg-brand-subtle hover:bg-brand-line"}`} data-testid={`status-${o.order_id}-${s}`}>{s}</button>
              ))}
            </div>
          </div>
          <ReturnRefundPanel order={o} reload={reload} />
          <ShiprocketRetryCard order={o} reload={reload} />
          <DeleteOrderCard order={o} reload={reload} />
        </div>
      )}
    </div>
  );
}

/**
 * iter93 — Retry Shiprocket panel.
 *
 * Shown for orders that either have a recorded `shiprocket_last_error`
 * or have no `shiprocket_shipment_id` yet.  Two actions:
 *  • Preview payload — dry-run the resolver against the live products
 *    collection and show the exact `sku` we would send.
 *  • Retry — hit /admin/orders/{id}/create-shipment (with force=True),
 *    which reuses the same idempotent flow used at order-placement
 *    time.  Never creates a duplicate website order.
 */
function ShiprocketRetryCard({ order, reload }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");

  // Only show the card when it's actionable — no shipment yet OR the
  // last attempt errored.  Successful shipments hide it automatically.
  const alreadyShipped = !!order.shiprocket_shipment_id;
  const lastErr = order.shiprocket_last_error || "";
  if (alreadyShipped && !lastErr) return null;

  const doPreview = async () => {
    setBusy(true);
    setPreviewErr("");
    setPreview(null);
    try {
      const { data } = await api.get(`/admin/orders/${order.order_id}/shiprocket-preview`);
      setPreview(data);
    } catch (e) {
      setPreviewErr(e?.response?.data?.detail || e?.message || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const doRetry = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.order_id}/create-shipment`);
      toast.success(`Shiprocket shipment created for ${order.order_id}`);
      reload && reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-brand-line rounded-xl p-4 bg-white" data-testid={`shiprocket-retry-panel-${order.order_id}`}>
      <div className="text-sm font-semibold text-brand-dark mb-1">Shiprocket shipment</div>
      {alreadyShipped ? (
        <div className="text-xs text-brand-muted mb-2">Shipment ID: <span className="font-mono">{order.shiprocket_shipment_id}</span></div>
      ) : (
        <div className="text-xs text-brand-muted mb-2">No Shiprocket shipment created yet.</div>
      )}
      {/* Session K / iter108 — surface the courier + AWB so ops can
          reconcile with Shiprocket at a glance.  No pickup info, no
          courier_id (internal only). */}
      {(order.courier_name || order.awb_code) && (
        <div className="text-xs bg-brand-subtle/40 border border-brand-line rounded-lg p-3 mb-3 space-y-0.5"
             data-testid={`shiprocket-courier-awb-${order.order_id}`}>
          <div><span className="text-brand-muted">Courier:</span>{" "}
            <span className="font-semibold text-brand-dark" data-testid={`courier-name-${order.order_id}`}>
              {order.courier_name || "—"}
            </span>
          </div>
          <div><span className="text-brand-muted">AWB:</span>{" "}
            <span className="font-mono text-brand-dark" data-testid={`awb-${order.order_id}`}>
              {order.awb_code || "—"}
            </span>
          </div>
          {order.courier_mismatch && (
            <div className="mt-2 px-2 py-1.5 rounded bg-red-50 border border-red-200 text-red-800"
                 data-testid={`courier-mismatch-${order.order_id}`}>
              ⚠ Courier drift: quoted <b>{order.courier_mismatch_locked || "—"}</b> but Shiprocket
              assigned <b>{order.courier_mismatch_assigned || order.courier_name}</b>. Please review.
            </div>
          )}
        </div>
      )}
      {lastErr && (
        <div className="text-xs text-brand-chili bg-brand-chili/5 border border-brand-chili/20 rounded-lg px-3 py-2 mb-3 whitespace-pre-wrap" data-testid={`shiprocket-last-error-${order.order_id}`}>
          Last error: {lastErr}
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-3">
        <button
          type="button"
          onClick={doPreview}
          disabled={busy}
          className="btn-secondary text-xs disabled:opacity-60"
          data-testid={`shiprocket-preview-btn-${order.order_id}`}
        >
          Preview payload
        </button>
        <button
          type="button"
          onClick={doRetry}
          disabled={busy || alreadyShipped}
          className="btn-primary text-xs disabled:opacity-60"
          data-testid={`shiprocket-retry-btn-${order.order_id}`}
        >
          {alreadyShipped ? "Already shipped" : "Retry Shiprocket"}
        </button>
      </div>
      {previewErr && (
        <div className="text-xs text-brand-chili">{previewErr}</div>
      )}
      {preview && (
        <div className="text-xs bg-brand-subtle/40 border border-brand-line rounded-lg p-3 space-y-2" data-testid={`shiprocket-preview-result-${order.order_id}`}>
          <div className="font-semibold text-brand-dark">Rows to send: {preview.distinct_skus}</div>
          <table className="w-full text-left">
            <thead>
              <tr className="text-brand-muted">
                <th className="py-1">Item</th>
                <th className="py-1">Variant</th>
                <th className="py-1">Qty</th>
                <th className="py-1">SKU</th>
                <th className="py-1">Source</th>
              </tr>
            </thead>
            <tbody>
              {preview.resolved_items.map((r, i) => (
                <tr key={i} className="border-t border-brand-line">
                  <td className="py-1">{r.name}</td>
                  <td className="py-1">{r.variant_label || "—"}</td>
                  <td className="py-1">{r.quantity}</td>
                  <td className="py-1 font-mono">{r.final_sku}</td>
                  <td className="py-1 text-brand-muted">{r.sku_source}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {preview.distinct_skus < preview.resolved_items.length && (
            <div className="text-brand-muted">
              Note: {preview.resolved_items.length - preview.distinct_skus} row(s) will be merged (identical SKU + price).
            </div>
          )}

          {preview.weight && (
            <div
              className="mt-2 pt-2 border-t border-brand-line"
              data-testid={`shiprocket-preview-weight-${order.order_id}`}
            >
              <div className="font-semibold text-brand-dark mb-1">Shipment weight</div>
              <div className="grid grid-cols-3 gap-2 text-brand-dark">
                <div>
                  Contents<br />
                  <span className="font-mono" data-testid={`preview-weight-contents-${order.order_id}`}>
                    {preview.weight.contents_kg} kg
                  </span>
                </div>
                <div>
                  Packaging<br />
                  <span className="font-mono" data-testid={`preview-weight-packaging-${order.order_id}`}>
                    {preview.weight.packaging_kg} kg
                  </span>
                  {preview.weight.tier_used?.label && (
                    <span className="text-brand-muted"> ({preview.weight.tier_used.label})</span>
                  )}
                </div>
                <div>
                  <b>Total sent to Shiprocket</b><br />
                  <span className="font-mono font-semibold" data-testid={`preview-weight-total-${order.order_id}`}>
                    {preview.weight.total_kg} kg
                  </span>
                </div>
              </div>
              {!preview.weight.packaging_tiers_configured && (
                <div className="mt-1 text-brand-muted italic">
                  No packaging tiers configured — using 0.05 kg conservative buffer. Configure real
                  values in Admin → Settings → &quot;Shiprocket Packaging Weight&quot;.
                </div>
              )}
              {preview.weight.breakdown?.some((b) => b.is_combo) && (
                <div className="mt-1 text-brand-dark">
                  <span className="font-semibold">Combos:</span>
                  {preview.weight.breakdown
                    .filter((b) => b.is_combo)
                    .map((b, k) => (
                      <span key={k} className="ml-2">
                        {b.name} × {b.quantity} = {b.line_weight_kg} kg
                      </span>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}




/**
 * iter78 — Delete Order (permanent, TOTP-gated).
 *  Flow:  Click "Delete order" → confirm modal → 2FA prompt (6-digit code
 *  from Google Authenticator) → DELETE /api/admin/orders/:id with the code.
 *  The order is only removed if the code validates against the admin's
 *  live TOTP secret on the server; wrong / stale code aborts with a 401.
 */
function DeleteOrderCard({ order, reload }) {
  const [step, setStep] = useState("idle"); // idle | confirm | totp
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);

  const open = () => { setStep("confirm"); setOtp(""); };
  const close = () => { if (!busy) { setStep("idle"); setOtp(""); } };

  const proceedToTotp = () => setStep("totp");

  const confirmDelete = async () => {
    if (!/^\d{6}$/.test(otp)) { toast.error("Enter the 6-digit code from your authenticator app."); return; }
    setBusy(true);
    try {
      await api.delete(`/admin/orders/${order.order_id}`, { data: { totp_code: otp } });
      toast.success(`Order ${order.order_id} deleted permanently.`);
      setStep("idle");
      setOtp("");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-red-200 pt-4">
      <h4 className="font-medium mb-2 text-red-700">Danger zone</h4>
      <button
        type="button"
        onClick={open}
        className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-red-600 text-red-700 hover:bg-red-50 transition"
        data-testid={`delete-order-btn-${order.order_id}`}
      >
        <Trash2 className="w-3.5 h-3.5" /> Delete order
      </button>

      {step !== "idle" && (
        <div
          className="fixed inset-0 z-[200] bg-brand-dark/55 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
          data-testid="delete-order-modal"
        >
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4">
            {step === "confirm" && (
              <>
                <h3 className="font-serif text-2xl text-brand-dark">Delete this order?</h3>
                <p className="text-sm text-brand-muted leading-relaxed">
                  This will <b>permanently</b> remove <span className="font-mono text-brand-dark">{order.order_id}</span> from the database. This cannot be undone.
                </p>
                <div className="text-xs bg-brand-subtle rounded-lg p-3 text-brand-muted">
                  <div><b>Customer:</b> {order.user_name || order.address?.full_name || "—"}</div>
                  <div><b>Total:</b> ₹{Number(order.total || 0).toFixed(0)}</div>
                  <div><b>Placed:</b> {new Date(order.created_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center justify-end gap-3 pt-2">
                  <button type="button" onClick={close} className="btn-secondary" data-testid="delete-order-cancel">Cancel</button>
                  <button
                    type="button"
                    onClick={proceedToTotp}
                    className="px-5 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-medium"
                    data-testid="delete-order-confirm"
                  >
                    Delete
                  </button>
                </div>
              </>
            )}
            {step === "totp" && (
              <>
                <h3 className="font-serif text-2xl text-brand-dark">Enter your 2FA code</h3>
                <p className="text-sm text-brand-muted leading-relaxed">
                  Open Google Authenticator and enter the current 6-digit code for your admin account to authorise this deletion.
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  autoFocus
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  className="input-field text-center text-2xl font-mono tracking-[0.4em]"
                  data-testid="delete-order-totp-input"
                />
                <div className="flex items-center justify-end gap-3 pt-2">
                  <button type="button" onClick={close} disabled={busy} className="btn-secondary" data-testid="delete-order-totp-cancel">Cancel</button>
                  <button
                    type="button"
                    onClick={confirmDelete}
                    disabled={busy || !/^\d{6}$/.test(otp)}
                    className="px-5 py-2.5 rounded-full bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium"
                    data-testid="delete-order-totp-confirm"
                  >
                    {busy ? "Deleting…" : "Delete permanently"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ReturnRefundPanel({ order, reload }) {
  const [reason, setReason] = useState(order.return_reason || "");
  const [notes, setNotes] = useState(order.internal_notes || "");
  const [amount, setAmount] = useState(order.refund_amount != null ? String(order.refund_amount) : String(order.total || ""));
  const [refId, setRefId] = useState(order.refund_ref_id || "");
  const [busy, setBusy] = useState(false);

  const callAction = async (action) => {
    setBusy(true);
    try {
      const body = {
        action,
        return_reason: reason || "",
        internal_notes: notes || "",
      };
      if (action === "initiate_refund" || action === "complete_refund") {
        const n = Number(amount);
        if (!Number.isFinite(n) || n <= 0) {
          toast.error("Enter a valid refund amount");
          setBusy(false); return;
        }
        body.refund_amount = n;
        if (refId) body.refund_ref_id = refId;
      }
      const { data } = await api.post(`/admin/orders/${order.order_id}/return-action`, body);
      if (data?.razorpay_refund?.id) {
        toast.success(`Razorpay refund initiated · ${data.razorpay_refund.id}`);
      } else {
        toast.success(`Order updated → ${action.replace("_", " ")}`);
      }
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const history = Array.isArray(order.return_history) ? order.return_history : [];
  const isOnline = order.payment_method === "razorpay" && order.payment_status === "paid";
  const rzpRefundId = order.razorpay_refund_id || "";
  const rzpRefundStatus = order.razorpay_refund_status || "";

  return (
    <div className="border border-brand-line rounded-xl p-4 bg-white" data-testid={`return-refund-panel-${order.order_id}`}>
      <h4 className="font-medium mb-3 flex items-center gap-2">
        <RefreshCw className="w-4 h-4 text-brand-chili" strokeWidth={1.6} />
        Return &amp; Refund Management
      </h4>

      {/* Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="label-field">Return reason (customer / admin)</label>
          <textarea
            value={reason} onChange={(e) => setReason(e.target.value)}
            rows={2} className="input-field" placeholder="e.g. Jar arrived broken; oil leakage"
            data-testid={`rr-reason-${order.order_id}`}
          />
        </div>
        <div>
          <label className="label-field">Internal notes (admin only)</label>
          <textarea
            value={notes} onChange={(e) => setNotes(e.target.value)}
            rows={2} className="input-field" placeholder="Photo verified on WhatsApp, RTO logged…"
            data-testid={`rr-notes-${order.order_id}`}
          />
        </div>
        <div>
          <label className="label-field">Refund amount (₹)</label>
          <input
            type="number" min="0" step="0.01"
            value={amount} onChange={(e) => setAmount(e.target.value)}
            className="input-field" placeholder={`Order total ₹${Math.round(order.total || 0)}`}
            data-testid={`rr-amount-${order.order_id}`}
          />
          {isOnline && (
            <p className="text-[11px] text-brand-muted mt-1">Razorpay refund will be auto-initiated using this amount.</p>
          )}
        </div>
        <div>
          <label className="label-field">Refund reference ID (manual / UTR)</label>
          <input
            type="text"
            value={refId} onChange={(e) => setRefId(e.target.value)}
            className="input-field" placeholder="rfnd_XXX or NEFT UTR"
            data-testid={`rr-refid-${order.order_id}`}
          />
        </div>
      </div>

      {/* Razorpay status panel */}
      {isOnline && (
        <div className="mb-3 text-xs flex flex-wrap items-center gap-3 bg-blue-50/60 border border-blue-100 rounded-lg px-3 py-2">
          <span className="font-medium text-blue-900">Razorpay</span>
          <span>Payment ID: <span className="font-mono">{order.razorpay_payment_id || "—"}</span></span>
          {rzpRefundId && <span>Refund ID: <span className="font-mono">{rzpRefundId}</span></span>}
          {rzpRefundStatus && <span>Status: <span className="font-medium">{rzpRefundStatus}</span></span>}
        </div>
      )}
      {!isOnline && order.payment_method === "cod" && (
        <div className="mb-3 text-xs flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-amber-900">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" strokeWidth={1.7} />
          COD order — process refund manually (UPI/NEFT/IMPS) and record the UTR in the reference ID above.
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        {RETURN_ACTIONS.map((a) => (
          <button
            key={a.action}
            onClick={() => callAction(a.action)}
            disabled={busy || order.order_status === a.target}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${a.color} disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-95`}
            data-testid={`rr-action-${order.order_id}-${a.action}`}
            title={order.order_status === a.target ? "Already at this status" : ""}
          >
            {a.label}
          </button>
        ))}
      </div>

      {/* History timeline */}
      {history.length > 0 && (
        <div className="mt-4 pt-4 border-t border-brand-line">
          <h5 className="font-medium text-xs uppercase tracking-wider text-brand-muted mb-2">Return / Refund history</h5>
          <ul className="space-y-2" data-testid={`rr-history-${order.order_id}`}>
            {history.map((h, idx) => (
              <li key={`${h.status}-${h.at || idx}`} className="text-xs flex flex-col bg-brand-cream/40 rounded-lg px-3 py-2">
                <div className="flex justify-between">
                  <span className="font-medium">{h.label || h.status}</span>
                  <span className="text-brand-muted">{new Date(h.at).toLocaleString()}</span>
                </div>
                {h.return_reason && <span className="text-brand-muted">Reason: {h.return_reason}</span>}
                {h.refund_amount != null && <span className="text-brand-muted">Amount: ₹{Number(h.refund_amount).toFixed(0)}</span>}
                {h.refund_ref_id && <span className="text-brand-muted">Ref: {h.refund_ref_id}</span>}
                {h.razorpay_refund_id && <span className="text-brand-muted">Razorpay: {h.razorpay_refund_id} · {h.razorpay_refund_status}</span>}
                {h.internal_notes && <span className="text-brand-muted italic">Note: {h.internal_notes}</span>}
                {h.error && <span className="text-red-700">{h.error}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
